import React, { FunctionComponent } from "react";
import { graphql } from "gatsby";
import { Layout } from "../../components/layout";
import { PageHeader } from "../../components/pageHeader";
import { ProgressBar } from "../../components/projectCard";
import { PostGrid } from "../../components/postCard";
import { SEO } from "../../components/seo";
import {
  PostSnippetNode,
  ProjectPartNode,
  ProjectSummaryNode,
  toPostSnippet,
  toProjectSummaries,
} from "../../data/posts";
import { pluralize } from "../../data/format";
import { TOPICS } from "../../topics";

export const pageQuery = graphql`
  query ProjectBySlug($slug: String!) {
    project: allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "projects" } }
        frontmatter: { slug: { eq: $slug } }
      }
    ) {
      nodes {
        ...ProjectSummary
      }
    }
    posts: allMarkdownRemark(
      filter: {
        fields: { collection: { eq: "blog" } }
        frontmatter: { projects: { elemMatch: { slug: { eq: $slug } } } }
      }
    ) {
      nodes {
        ...PostSnippet
        ...ProjectPart
      }
    }
  }
`;

interface Page {
  data: {
    project: { nodes: ProjectSummaryNode[] };
    posts: { nodes: (PostSnippetNode & ProjectPartNode)[] };
  };
}

export const Page: FunctionComponent<Page> = ({ data }) => {
  const [project] = toProjectSummaries(data.project.nodes, data.posts.nodes);
  const snippets = data.posts.nodes.map(toPostSnippet);
  // Posts in part order, matching the progress bar.
  const posts = project.parts.flatMap(
    (part) => snippets.find((post) => post.href === part.href) || []
  );

  return (
    <>
      <SEO
        title={project.title}
        description={project.summary}
        image={project.img.src}
      />
      <Layout>
        <PageHeader
          title={project.title}
          deck={project.summary}
          topic={project.topic}
        >
          <div className="font-display text-label font-semibold uppercase text-muted">
            Project · {TOPICS[project.topic].shortName} ·{" "}
            {pluralize(project.parts.length, "part", "parts")}
          </div>
          <div className="max-w-[760px]">
            <ProgressBar
              topic={project.topic}
              total={project.parts.length}
              filled={project.parts.length}
            />
          </div>
        </PageHeader>
        <div className="px-gutter-sm pb-14 md:px-gutter">
          <PostGrid posts={posts} />
        </div>
      </Layout>
    </>
  );
};

export default Page;
